import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, X, Volume2, VolumeX, Share2, Crown, Clock, Check, ShoppingBag, Film, Play } from "lucide-react";
import { api } from "../lib/api";
import StorefrontHeader from "../components/StorefrontHeader";
import useEmbedResize, { isEmbedded } from "../hooks/useEmbedResize";

const STORE_EMAIL_KEY = "launchpad:storefront:email";
const VOTED_KEY = "launchpad:storefront:voted";

function loadVotedMap() {
  try { return JSON.parse(localStorage.getItem(VOTED_KEY) || "{}"); } catch { return {}; }
}
function saveVotedMap(m) {
  try { localStorage.setItem(VOTED_KEY, JSON.stringify(m)); } catch { /* ignore */ }
}

function detectKind(url) {
  if (!url) return null;
  const u = url.toLowerCase();
  if (u.includes("youtube.com") || u.includes("youtu.be")) return "youtube";
  if (u.includes("vimeo.com")) return "vimeo";
  return "native";
}
function youtubeEmbed(url, muted) {
  const m =
    url.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/) ||
    url.match(/[?&]v=([A-Za-z0-9_-]{6,})/) ||
    url.match(/embed\/([A-Za-z0-9_-]{6,})/) ||
    url.match(/shorts\/([A-Za-z0-9_-]{6,})/);
  const id = m ? m[1] : null;
  if (!id) return url;
  return `https://www.youtube.com/embed/${id}?autoplay=1&mute=${muted ? 1 : 0}&loop=1&playlist=${id}&controls=0&modestbranding=1&rel=0&playsinline=1`;
}
function vimeoEmbed(url, muted) {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/) || url.match(/player\.vimeo\.com\/video\/(\d+)/);
  const id = m ? m[1] : null;
  if (!id) return url;
  return `https://player.vimeo.com/video/${id}?autoplay=1&muted=${muted ? 1 : 0}&loop=1&background=0&playsinline=1`;
}

function timeLeftLabel(endDate) {
  const ms = new Date(endDate).getTime() - Date.now();
  if (ms <= 0) return "Ended";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (d > 0) return `${d}d ${h}h left`;
  if (h > 0) return `${h}h ${m}m left`;
  return `${m}m left`;
}

/* ------------------------------------------------------------------ */
/* Mobile: TikTok-style vertical scroll snap slide                    */
/* ------------------------------------------------------------------ */
function ReelSlide({ campaign, index, total, rank, isWinner, isActive, muted, onToggleMute, onVote, voted, onSwipeUp }) {
  const url = campaign.videoUrl || campaign.productVideoUrl;
  const kind = detectKind(url);
  const touchStart = useRef(null);

  const handleTouchStart = (e) => {
    if (!e.touches?.[0]) return;
    touchStart.current = { y: e.touches[0].clientY, t: Date.now() };
  };
  const handleTouchEnd = (e) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start) return;
    const end = e.changedTouches?.[0];
    if (!end) return;
    const dy = start.y - end.clientY;
    const dt = Date.now() - start.t;
    if (dy > 80 && dt < 600) {
      onSwipeUp?.();
    }
  };

  return (
    <div
      className="relative h-full w-full bg-black snap-start shrink-0 overflow-hidden"
      style={{ height: "100%" }}
      data-testid={`reel-slide-${index}`}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {url ? (
        kind === "youtube" ? (
          <iframe
            src={youtubeEmbed(url, muted)}
            title={campaign.productTitle}
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            style={{ pointerEvents: "none" }}
            allow="autoplay; encrypted-media; picture-in-picture"
            frameBorder="0"
          />
        ) : kind === "vimeo" ? (
          <iframe
            src={vimeoEmbed(url, muted)}
            title={campaign.productTitle}
            className="absolute inset-0 w-full h-full"
            style={{ pointerEvents: "none" }}
            allow="autoplay; fullscreen; picture-in-picture"
            frameBorder="0"
          />
        ) : (
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src={url}
            poster={campaign.productImageUrl || undefined}
            autoPlay
            muted={muted}
            loop
            playsInline
            onClick={onToggleMute}
          />
        )
      ) : campaign.productImageUrl ? (
        <img src={campaign.productImageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : null}

      <button
        type="button"
        onClick={onToggleMute}
        className="absolute inset-0 bg-transparent"
        aria-label="Toggle sound"
        data-testid={`reel-tap-${index}`}
      />

      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/70 to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none" />

      <div className="absolute top-0 left-0 right-0 px-4 pt-4 flex items-center gap-1 pointer-events-none">
        {Array.from({ length: total }).map((_, i) => (
          <div key={i} className={`flex-1 h-1 rounded-full ${i === index ? "bg-white" : "bg-white/30"}`} />
        ))}
      </div>

      <div className="absolute top-7 left-4 mt-3 pointer-events-none">
        <div className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 ${
          rank === 1 ? "bg-amber-500 text-white" :
          isWinner ? "bg-amber-100 text-amber-900" : "bg-white/15 text-white backdrop-blur-md border border-white/20"
        }`}>
          {isWinner && rank === 1 && <Crown size={12} />}
          #{rank}
          {isWinner && isActive && <span className="ml-1 opacity-80">· winning</span>}
        </div>
      </div>

      <div className="absolute top-7 right-4 mt-3 pointer-events-none">
        <div className="w-9 h-9 rounded-full bg-black/40 backdrop-blur-md border border-white/20 flex items-center justify-center text-white">
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </div>
      </div>

      <div className="absolute bottom-32 right-3 flex flex-col items-center gap-5 z-10">
        <button
          type="button"
          onClick={onVote}
          data-testid={`reel-vote-btn-${index}`}
          className="flex flex-col items-center gap-1 group"
          aria-label={voted ? "Already voted" : "Vote"}
        >
          <div
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 ${
              voted
                ? "bg-rose-500 text-white shadow-[0_0_24px_rgba(244,63,94,0.6)]"
                : "bg-white/15 backdrop-blur-md border border-white/30 text-white group-hover:scale-110 group-active:scale-95"
            }`}
          >
            <Heart size={28} fill={voted ? "white" : "none"} />
          </div>
          <span className="text-xs font-medium text-white tabular-nums" data-testid={`reel-votes-${index}`}>
            {campaign.currentVoteCount}
          </span>
        </button>
        <button
          type="button"
          onClick={() => {
            const link = `${window.location.origin}/storefront/reels/${campaign.roundId || ""}`;
            navigator.clipboard.writeText(link);
            toast.success("Reel link copied!");
          }}
          className="flex flex-col items-center gap-1 group"
          aria-label="Share"
        >
          <div className="w-12 h-12 rounded-full bg-white/15 backdrop-blur-md border border-white/30 text-white flex items-center justify-center group-hover:scale-110 group-active:scale-95 transition-all">
            <Share2 size={20} />
          </div>
          <span className="text-[10px] font-medium text-white">Share</span>
        </button>
      </div>

      <div className="absolute bottom-0 left-0 right-16 p-5 pb-6 text-white pointer-events-none">
        <div className="text-[11px] uppercase tracking-widest text-white/70 mb-1">Upcoming launch</div>
        <h2 className="text-xl font-semibold tracking-tight leading-snug" data-testid={`reel-title-${index}`}>
          {campaign.productTitle}
        </h2>
        {campaign.preOrderPrice && (
          <div className="text-base text-white/80 mt-1">${campaign.preOrderPrice.toFixed(2)}</div>
        )}
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {campaign.status === "pre_order" && (
            <Link
              to={`/storefront/${campaign.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-black text-xs font-semibold pointer-events-auto hover:bg-gray-100"
              data-testid={`reel-preorder-link-${index}`}
            >
              <ShoppingBag size={12} /> Pre-order now
            </Link>
          )}
          {campaign.status === "closed" && (
            <span className="lp-badge lp-badge-closed">Didn&apos;t make it</span>
          )}
        </div>
        <div className="text-[10px] text-white/50 mt-3">↑ Swipe up to vote · tap to {muted ? "unmute" : "mute"}</div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Desktop: video-card grid (competitions-style)                       */
/* ------------------------------------------------------------------ */
function DesktopVideoCard({ campaign, rank, isWinner, isActive, totalVotes, youVoted, onVote, onExpand }) {
  const url = campaign.videoUrl || campaign.productVideoUrl;
  const kind = detectKind(url);
  const [muted, setMuted] = useState(true);
  const pct = totalVotes > 0 ? Math.round((campaign.currentVoteCount / totalVotes) * 100) : 0;
  const closedOrPre = campaign.status === "pre_order" || campaign.status === "closed";

  return (
    <div
      className={`lp-card overflow-hidden flex flex-col ${isWinner && isActive ? "ring-2 ring-amber-400" : ""}`}
      data-testid={`reel-card-${campaign.id}`}
    >
      {/* Video preview — 9:16 aspect for vertical videos, contained in card */}
      <div className="relative bg-black overflow-hidden group" style={{ aspectRatio: "9 / 12" }}>
        {url ? (
          kind === "youtube" ? (
            <iframe
              src={youtubeEmbed(url, true)}
              title={campaign.productTitle}
              className="absolute inset-0 w-full h-full pointer-events-none"
              allow="autoplay; encrypted-media; picture-in-picture"
              frameBorder="0"
            />
          ) : kind === "vimeo" ? (
            <iframe
              src={vimeoEmbed(url, true)}
              title={campaign.productTitle}
              className="absolute inset-0 w-full h-full pointer-events-none"
              allow="autoplay; fullscreen; picture-in-picture"
              frameBorder="0"
            />
          ) : (
            <video
              className="absolute inset-0 w-full h-full object-cover"
              src={url}
              poster={campaign.productImageUrl || undefined}
              autoPlay
              muted={muted}
              loop
              playsInline
            />
          )
        ) : campaign.productImageUrl ? (
          <img src={campaign.productImageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/40 text-xs">No video</div>
        )}

        {/* Rank badge */}
        <div className={`absolute top-3 left-3 w-10 h-10 rounded-full flex items-center justify-center text-base font-bold shadow-lg ${
          rank === 1 ? "bg-amber-500 text-white" :
          isWinner ? "bg-amber-100 text-amber-900" : "bg-white text-gray-900"
        }`}>
          {rank}
        </div>

        {/* Status badges */}
        <div className="absolute top-3 right-3 flex flex-col gap-1.5 items-end">
          {isWinner && isActive && (
            <span className="lp-badge lp-badge-preorder shadow-md">In winning zone</span>
          )}
          {campaign.status === "pre_order" && (
            <span className="lp-badge lp-badge-preorder shadow-md">Pre-order</span>
          )}
          {campaign.status === "closed" && (
            <span className="lp-badge lp-badge-closed shadow-md">Didn&apos;t make it</span>
          )}
        </div>

        {/* Expand overlay button — bottom right */}
        {url && kind === "native" && (
          <button
            type="button"
            onClick={() => setMuted((m) => !m)}
            className="absolute bottom-3 left-3 w-9 h-9 rounded-full bg-black/40 backdrop-blur-md border border-white/20 text-white flex items-center justify-center hover:bg-black/60"
            aria-label="Toggle sound"
            data-testid={`reel-card-mute-${campaign.id}`}
          >
            {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
          </button>
        )}
        <button
          type="button"
          onClick={onExpand}
          className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-black text-[11px] font-semibold hover:bg-white shadow-lg"
          aria-label="Open fullscreen"
          data-testid={`reel-card-expand-${campaign.id}`}
        >
          <Play size={12} fill="currentColor" /> Watch
        </button>
      </div>

      {/* Body */}
      <div className="p-5 flex flex-col gap-3 flex-1">
        <div>
          <h3 className="text-lg font-semibold tracking-tight text-gray-900 line-clamp-2" data-testid={`reel-card-title-${campaign.id}`}>
            {campaign.productTitle}
          </h3>
          {campaign.preOrderPrice && (
            <div className="text-sm text-gray-500 mt-1">${campaign.preOrderPrice.toFixed(2)}</div>
          )}
        </div>

        {/* Vote count + progress */}
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-sm">
              <strong className="text-2xl font-semibold tabular-nums tracking-tight" data-testid={`reel-card-votes-${campaign.id}`}>
                {campaign.currentVoteCount}
              </strong>
              <span className="text-gray-500 text-xs ml-1.5">vote{campaign.currentVoteCount === 1 ? "" : "s"}</span>
            </span>
            <span className="text-xs text-gray-500 tabular-nums">{pct}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#d0473e] transition-all duration-500"
              style={{ width: `${Math.min(100, pct)}%` }}
            />
          </div>
        </div>

        {/* CTA */}
        <div className="mt-auto pt-2">
          {!isActive ? (
            campaign.status === "pre_order" ? (
              <Link to={`/storefront/${campaign.id}`} className="lp-btn-primary w-full justify-center" data-testid={`reel-card-preorder-${campaign.id}`}>
                <ShoppingBag size={14} /> Pre-order
              </Link>
            ) : (
              <button className="lp-btn-secondary w-full justify-center" disabled>Closed</button>
            )
          ) : closedOrPre ? (
            <Link to={`/storefront/${campaign.id}`} className="lp-btn-secondary w-full justify-center">View</Link>
          ) : (
            <button
              type="button"
              onClick={onVote}
              data-testid={`reel-card-vote-${campaign.id}`}
              className={`w-full inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-all ${
                youVoted
                  ? "bg-rose-500 text-white"
                  : "bg-[#111] text-white hover:bg-[#d0473e]"
              }`}
            >
              <Heart size={14} fill={youVoted ? "white" : "none"} />
              {youVoted ? "Voted" : "Vote"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Fullscreen mobile-style overlay (used on desktop "Watch" click)     */
/* ------------------------------------------------------------------ */
function FullscreenPlayer({ campaigns, startIndex, muted, setMuted, voted, onVote, winnersCount, isActive, onClose }) {
  const scrollerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(startIndex);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    // Jump to the requested slide once mounted
    el.scrollTo({ top: startIndex * el.clientHeight, behavior: "auto" });
    const handler = () => {
      const idx = Math.round(el.scrollTop / el.clientHeight);
      setActiveIndex(idx);
    };
    el.addEventListener("scroll", handler, { passive: true });
    return () => el.removeEventListener("scroll", handler);
  }, [startIndex]);

  return (
    <div className="fixed inset-0 z-50 bg-black flex items-center justify-center" data-testid="reel-fullscreen">
      <div className="relative w-full h-full md:w-[420px] md:max-w-[420px] md:h-[85vh] md:rounded-3xl overflow-hidden md:shadow-2xl bg-black">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-30 w-9 h-9 rounded-full bg-black/40 backdrop-blur-md border border-white/20 text-white flex items-center justify-center hover:bg-black/60"
          aria-label="Close"
          data-testid="reel-fullscreen-close"
        >
          <X size={18} />
        </button>

        <button
          onClick={() => setMuted((m) => !m)}
          className="absolute bottom-3 left-3 z-30 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-md border border-white/20 text-white text-[11px] font-medium flex items-center gap-1.5"
        >
          {muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
          {muted ? "Tap video for sound" : "Sound on"}
        </button>

        <div
          ref={scrollerRef}
          className="h-full w-full overflow-y-scroll snap-y snap-mandatory"
          style={{ scrollbarWidth: "none" }}
        >
          {campaigns.map((c, i) => (
            <div key={c.id} className="h-full snap-start" style={{ height: "100%" }}>
              <ReelSlide
                campaign={c}
                index={i}
                total={campaigns.length}
                rank={i + 1}
                isWinner={i < winnersCount}
                isActive={isActive}
                muted={muted}
                onToggleMute={() => setMuted((m) => !m)}
                onVote={() => onVote(c)}
                voted={!!voted[c.id]}
                onSwipeUp={() => onVote(c)}
              />
            </div>
          ))}
        </div>
        <div className="absolute top-4 left-4 z-20 text-[10px] text-white/60 uppercase tracking-widest">
          {activeIndex + 1} / {campaigns.length}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
export default function StorefrontReel() {
  useEmbedResize();
  const embedded = isEmbedded();
  const { id } = useParams();

  const [reel, setReel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState(() => localStorage.getItem(STORE_EMAIL_KEY) || "");
  const [emailDraft, setEmailDraft] = useState("");
  const [showEmailGate, setShowEmailGate] = useState(false);
  const [voted, setVoted] = useState(() => loadVotedMap());
  const [muted, setMuted] = useState(true);
  const [fullscreenStart, setFullscreenStart] = useState(null); // index or null

  const refresh = useCallback(async () => {
    try {
      const r = await api.publicGetReel(id);
      setReel(r);
    } catch {
      setReel(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!reel || reel.status !== "active") return;
    const t = setInterval(refresh, 10000);
    return () => clearInterval(t);
  }, [reel?.status, refresh]);

  const saveEmail = (e) => {
    e?.preventDefault();
    const clean = emailDraft.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      toast.error("Enter a valid email");
      return;
    }
    setEmail(clean);
    localStorage.setItem(STORE_EMAIL_KEY, clean);
    setShowEmailGate(false);
    toast.success("You're in — tap the heart to vote");
  };

  const handleVote = async (campaign) => {
    if (!email) {
      setShowEmailGate(true);
      return;
    }
    try {
      const r = await api.vote(campaign.id, email);
      const next = { ...voted, [campaign.id]: true };
      setVoted(next);
      saveVotedMap(next);
      if (!r.ok && r.reason === "already_voted") {
        toast("You're already backing this one");
      } else {
        toast.success("Voted!");
      }
      setReel((prev) => {
        if (!prev) return prev;
        const updated = (prev.campaigns || []).map((c) =>
          c.id === campaign.id && r.ok ? { ...c, currentVoteCount: r.currentVoteCount } : c
        );
        return { ...prev, campaigns: updated };
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not vote");
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center text-sm text-gray-500">Loading…</div>;
  }
  if (!reel) {
    return <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center text-sm text-gray-500">Reel not found</div>;
  }

  const campaigns = reel.campaigns || [];
  const totalVotes = campaigns.reduce((s, c) => s + (c.currentVoteCount || 0), 0);
  const tl = timeLeftLabel(reel.endDate);
  const isActive = reel.status === "active";

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="storefront-reel-page">
      <StorefrontHeader active="reels" />

      <main className={`max-w-6xl mx-auto px-6 md:px-10 ${embedded ? "py-6" : "py-10 md:py-14"}`}>
        {/* Hero */}
        <div className="rounded-3xl bg-gradient-to-br from-gray-900 to-gray-700 text-white p-8 md:p-10 lp-fade-in">
          <div className="flex items-center gap-2 text-rose-200 text-xs uppercase tracking-widest">
            <Film size={14} /> Video competition
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight mt-3" data-testid="reel-name">
            {reel.name}
          </h1>
          {reel.description && (
            <p className="text-sm md:text-base text-gray-300 mt-3 max-w-2xl leading-relaxed">{reel.description}</p>
          )}
          <div className="mt-6 flex items-center gap-6 flex-wrap text-sm">
            <div className="flex items-center gap-2">
              <Crown size={16} className="text-rose-200" />
              <span><strong>Top {reel.winnersCount}</strong> advance to pre-order</span>
            </div>
            <div className="flex items-center gap-2" data-testid="reel-countdown">
              <Clock size={16} className="text-rose-200" />
              <span>{tl}</span>
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                toast.success("Reel link copied!");
              }}
              className="ml-auto inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/20 hover:bg-white/10 transition-colors"
              data-testid="reel-share-btn"
            >
              <Share2 size={12} /> Share
            </button>
          </div>
        </div>

        {/* Email bar */}
        <div className="lp-card p-5 mt-6" data-testid="reel-email-bar">
          {email ? (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-sm text-gray-800">
                <Check size={16} className="text-emerald-600" />
                Voting as <strong>{email}</strong>
              </div>
              <button
                onClick={() => {
                  localStorage.removeItem(STORE_EMAIL_KEY);
                  setEmail("");
                  setEmailDraft("");
                }}
                className="lp-btn-secondary text-xs"
              >
                Change email
              </button>
            </div>
          ) : (
            <form onSubmit={saveEmail} className="flex items-end gap-3 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <label className="lp-label" htmlFor="reel-inline-email-input">Your email</label>
                <input
                  id="reel-inline-email-input"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="lp-input"
                  data-testid="reel-inline-email-input"
                  value={emailDraft}
                  onChange={(e) => setEmailDraft(e.target.value)}
                />
                <div className="lp-help">One vote per product. Save once, vote freely.</div>
              </div>
              <button type="submit" className="lp-btn-primary" data-testid="reel-inline-save-email-btn">
                Save & start voting
              </button>
            </form>
          )}
        </div>

        {/* Video grid — desktop shows all videos; mobile also uses the grid.
           Users can tap "Watch" on any card to open the fullscreen TikTok player. */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reel-video-grid">
          {campaigns.map((c, i) => (
            <DesktopVideoCard
              key={c.id}
              campaign={c}
              rank={i + 1}
              isWinner={i < reel.winnersCount}
              isActive={isActive}
              totalVotes={totalVotes}
              youVoted={!!voted[c.id]}
              onVote={() => handleVote(c)}
              onExpand={() => setFullscreenStart(i)}
            />
          ))}
          {campaigns.length === 0 && (
            <div className="lp-card p-12 text-center text-sm text-gray-500 sm:col-span-2 lg:col-span-3">
              No videos in this reel yet.
            </div>
          )}
        </div>

        {/* How it works footer */}
        <div className="mt-10 lp-card p-6 bg-gradient-to-br from-gray-50 to-white">
          <div className="flex items-center gap-2 mb-3">
            <Film size={16} className="text-[#d0473e]" />
            <h3 className="text-sm font-semibold">How this reel works</h3>
          </div>
          <ol className="text-xs text-gray-600 space-y-2 list-decimal list-inside leading-relaxed">
            <li>Watch each video — tap <strong>Watch</strong> for the fullscreen TikTok view with sound.</li>
            <li>Tap the <strong>heart</strong> to vote. One vote per product per email.</li>
            <li>When the timer hits zero, the <strong>top {reel.winnersCount}</strong> become available for pre-order.</li>
            <li>We&apos;ll email every voter for the winning products with checkout instructions.</li>
          </ol>
        </div>
      </main>

      {/* Fullscreen TikTok player overlay */}
      {fullscreenStart !== null && (
        <FullscreenPlayer
          campaigns={campaigns}
          startIndex={fullscreenStart}
          muted={muted}
          setMuted={setMuted}
          voted={voted}
          onVote={handleVote}
          winnersCount={reel.winnersCount}
          isActive={isActive}
          onClose={() => setFullscreenStart(null)}
        />
      )}

      {/* Email gate modal (triggered by mobile vote in fullscreen) */}
      {showEmailGate && !email && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6">
          <form
            onSubmit={saveEmail}
            className="w-full max-w-sm bg-white rounded-2xl p-6"
            data-testid="reel-email-gate"
          >
            <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-3">
              <Heart size={20} className="text-rose-500" />
            </div>
            <h3 className="text-lg font-semibold tracking-tight text-center">Vote with your email</h3>
            <p className="text-xs text-gray-500 text-center mt-1.5">
              One vote per product. We&apos;ll only email you if your pick wins pre-order.
            </p>
            <input
              type="email"
              required
              autoFocus
              placeholder="you@example.com"
              className="lp-input mt-4"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
              data-testid="reel-email-input"
            />
            <button type="submit" className="lp-btn-primary w-full justify-center mt-3" data-testid="reel-email-save-btn">
              <Check size={14} /> Save & vote
            </button>
            <button
              type="button"
              onClick={() => setShowEmailGate(false)}
              className="w-full text-xs text-gray-500 hover:text-gray-900 mt-3"
            >
              Cancel
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
